import { useMemo } from 'react';
import { useGetEventPhotoByEventHandleQuery } from './eventPhoto.graphql.generated';
import { EventDesignPurpose, EventType } from '@graphql/generated-base';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { isPhotoTombstoned } from './isPhotoTombstoned';

type UseEventPhoto = Maybe<{ id?: string; url?: string }>;
/**
 * To fetch the best photo URL for a given event handle.
 *
 * @param {string} [eventHandle] - The unique event handle.
 * @param {boolean} [shouldSkipCall] - Whether to skip fetching the photo.
 * @param {EventDesignPurpose} [purpose] - The event design purpose to fetch (defaults to `live`).
 */
export const useEventPhoto = (eventHandle?: string, shouldSkipCall?: boolean, purpose?: EventDesignPurpose) => {
  const { data, ...rest } = useGetEventPhotoByEventHandleQuery({
    variables: {
      website: eventHandle!,
      purpose: purpose ?? EventDesignPurpose.live,
    },
    skip: !eventHandle || shouldSkipCall,
    batchMode: 'fast',
  });

  const eventPhoto: UseEventPhoto = useExtractEventPhoto(data?.eventByName);
  const headerLayoutPhoto = extractHeaderLayoutPhoto(data?.eventByName);

  return { eventPhoto, headerLayoutPhoto, ...rest };
};

export interface SupportedHeaderPhotoLayoutSchema {
  photos?: ReadonlyArray<{ id: string; url: string }>;
}

type Event = {
  photo?: { id: string; url: string } | null | undefined;
  photoV2Url?: string | null;
  info: {
    eventType: EventType;
  };
  eventDesign?: Maybe<{
    activeWebsiteHeaderPresentationLayout?: Maybe<{
      dataJSON?: SupportedHeaderPhotoLayoutSchema;
    }>;
  }>;
};

/** Extracts the photo from the header presentation layout (where welcome page uploads are stored). */
export const extractHeaderLayoutPhoto = <T extends Event>(event?: Maybe<T>) => {
  const photo = (event?.eventDesign?.activeWebsiteHeaderPresentationLayout?.dataJSON as SupportedHeaderPhotoLayoutSchema)?.photos?.[0];
  return isPhotoTombstoned(photo) ? undefined : photo;
};

/** Extracts all photos from the header presentation layout. */
export const extractHeaderLayoutPhotos = <T extends Event>(event?: Maybe<T>): ReadonlyArray<{ id: string; url: string }> =>
  ((event?.eventDesign?.activeWebsiteHeaderPresentationLayout?.dataJSON as SupportedHeaderPhotoLayoutSchema)?.photos ?? []).filter(p => !isPhotoTombstoned(p));

export const extractEventPhoto = <T extends Event>(
  event?: Maybe<T>,
  isEventPhotoV2UrlMigrationEnabled?: boolean,
): { id: string | undefined; url: string | undefined } | undefined => {
  // Priority 1: photoV2Url — always read for `general` events; for other types only once
  // `eventPhotoV2UrlMigration` routes writes there too (same gate as getWelcomePagePhotoUrl).
  // Write paths (useShareOgPhotoPicker, usePhotoUploadFlow) mirror this read, so keep them in sync.
  if ((event?.info.eventType === EventType.general || isEventPhotoV2UrlMigrationEnabled) && event?.photoV2Url) {
    return { id: undefined, url: event.photoV2Url };
  }
  // Priority 2: photo (legacy photo object) — skip if tombstoned
  const eventPhoto = event?.photo;
  if (eventPhoto && !isPhotoTombstoned(eventPhoto)) {
    return { id: eventPhoto.id, url: eventPhoto.url };
  }
  // Priority 3: header layout photo (fallback to design system)
  const eventDesignPhoto = extractHeaderLayoutPhoto(event);
  if (eventDesignPhoto) {
    return { id: eventDesignPhoto.id, url: eventDesignPhoto.url };
  }
  return;
};

/**
 * Hook form of `extractEventPhoto` — resolves the `eventPhotoV2UrlMigration` flag internally.
 * Memoized so the result is a stable dep for downstream memos (e.g. Helmet head data).
 */
export const useExtractEventPhoto = <T extends Event>(event?: Maybe<T>) => {
  const [isEventPhotoV2UrlMigrationEnabled] = useIsFeatureEnabled('eventPhotoV2UrlMigration');
  return useMemo(() => extractEventPhoto(event, isEventPhotoV2UrlMigrationEnabled), [event, isEventPhotoV2UrlMigrationEnabled]);
};
